import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Shield, LogOut, User, Settings, ChevronDown, FolderOpen, Grid, Activity, Laptop, Scale, FileText, ShieldAlert, Database, Mail, FileWarning, Heart, Grid3x3, Stethoscope, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PrefetchLink } from "@/components/PrefetchLink";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import cyberRisksHero from "@/assets/cyber-risks-hero.jpg";
import faqHero from "@/assets/faq-hero.jpg";
import tarifHero from "@/assets/tarif-hero.jpg";
import weitereVersicherungenHero from "@/assets/weitere-versicherungen-hero.jpg";
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    user,
    isAdmin,
    signOut
  } = useAuth();

  // Load blog categories
  const {
    data: categories = []
  } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('blog_posts').select('category').eq('published', true);
      if (error) throw error;
      const categoryCounts: Record<string, number> = data.reduce((acc, post) => {
        if (post.category) {
          acc[post.category] = (acc[post.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      return Object.entries(categoryCounts).map(([name, count]) => ({
        name,
        slug: name.toLowerCase().replace(/\s+/g, '-'),
        count
      })).sort((a, b) => b.count - a.count);
    }
  });
  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };
  return <header role="banner">
      <nav className="sticky top-0 z-50 bg-gray-950/60 backdrop-blur-md border-b border-accent/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 bg-stone-950">
        <div className="flex items-center justify-between h-16 bg-stone-950">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="Praxis Versichert Logo - Cyberversicherung für Arztpraxen, Zahnarztpraxen und medizinische Einrichtungen" title="Cyberversicherung für Arztpraxen – Ihr Spezialist für medizinische IT-Sicherheit" className="h-8 w-auto" width="96" height="96" loading="eager" fetchPriority="high" />
            <span className="font-bold text-foreground text-xs sm:text-sm lg:text-sm xl:text-base">praxis-versichert.de</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            <PrefetchLink to="/" onClick={() => window.scrollTo(0, 0)} className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm">
              Home
            </PrefetchLink>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                Cyberrisiken
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-background/95 backdrop-blur-xl border-accent/20 z-50" align="start">
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Cyber-Risiken
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberrisiken" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <Grid3x3 className="mr-2 h-4 w-4" />
                    Cyber-Risiken Übersicht
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Häufige Schadensszenarien</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/ransomware-schutz" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <ShieldAlert className="mr-2 h-4 w-4 text-destructive" />
                    Ransomware-Angriffe
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/datenverlust" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Database className="mr-2 h-4 w-4 text-destructive" />
                    Datenverlust & Datendiebstahl
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/phishing-schutz" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Mail className="mr-2 h-4 w-4 text-destructive" />
                    Phishing-Attacken
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Finanzielle Risiken</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/dsgvo-bussgelder" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <FileWarning className="mr-2 h-4 w-4 text-orange-500" />
                    DSGVO-Bußgelder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/betriebsunterbrechung" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Activity className="mr-2 h-4 w-4 text-orange-500" />
                    Betriebsunterbrechung
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Spezielle Bedrohungen</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/patientendaten-diebstahl" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Heart className="mr-2 h-4 w-4 text-yellow-600" />
                    Patientendaten-Diebstahl
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                Praxistypen
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-background/95 backdrop-blur-xl border-accent/20 z-50" align="start">
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Cyberversicherung nach Praxistyp
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/hausarztpraxis" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Stethoscope className="mr-2 h-4 w-4" />
                    Hausarztpraxis
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/zahnarztpraxis" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Stethoscope className="mr-2 h-4 w-4" />
                    Zahnarztpraxis
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/facharztpraxis" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Stethoscope className="mr-2 h-4 w-4" />
                    Facharztpraxis
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/mvz" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Building className="mr-2 h-4 w-4" />
                    MVZ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/klinik" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Building className="mr-2 h-4 w-4" />
                    Klinik
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PrefetchLink to="/tarifwissen" prefetchImages={[tarifHero]} className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm">
              Tarife
            </PrefetchLink>
            <PrefetchLink to="/cyberversicherung/vergleichsrechner" className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm">
              Vergleich
            </PrefetchLink>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                Beratung
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background/95 backdrop-blur-xl border-accent/20 z-50" align="start">
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Beratung & Vergleich
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/vergleichsrechner" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <Scale className="mr-2 h-4 w-4" />
                    Vergleichsrechner
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Cyberversicherung in Ihrer Stadt
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <Grid className="mr-2 h-4 w-4" />
                    Alle Städte anzeigen
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Top Städte</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/berlin" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    Berlin
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/muenchen" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    München
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/hamburg" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    Hamburg
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/koeln" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    Köln
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/cyberversicherung/frankfurt" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    Frankfurt
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                Versicherungen
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background/95 backdrop-blur-xl border-accent/20 z-50" align="start">
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Versicherungen für Arztpraxen
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <Grid className="mr-2 h-4 w-4" />
                    Alle Versicherungen anzeigen
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Pflichtversicherungen</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/berufshaftpflicht" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Shield className="mr-2 h-3 w-3 text-destructive" />
                    Berufshaftpflicht
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/betriebshaftpflicht" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Shield className="mr-2 h-3 w-3 text-destructive" />
                    Betriebshaftpflicht
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Empfohlene Versicherungen</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/inhaltsversicherung" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <FileText className="mr-2 h-3 w-3 text-accent" />
                    Inhaltsversicherung
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/rechtsschutz" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Scale className="mr-2 h-3 w-3 text-accent" />
                    Rechtsschutzversicherung
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Ergänzende Versicherungen</DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/praxisausfall" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Activity className="mr-2 h-3 w-3 text-warning" />
                    Praxisausfallversicherung
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/weitere-versicherungen/elektronik" className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                    <Laptop className="mr-2 h-3 w-3 text-warning" />
                    Elektronikversicherung
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                Blog
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background/95 backdrop-blur-xl border-accent/20 z-50 max-h-[400px] overflow-y-auto" align="start">
                <DropdownMenuLabel className="text-foreground font-semibold">
                  Blog Navigation
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-accent/20" />
                
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <FolderOpen className="mr-2 h-4 w-4" />
                    Alle Artikel
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuItem asChild>
                  <Link to="/blog/kategorien" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                    <Grid className="mr-2 h-4 w-4" />
                    Kategorien-Übersicht
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-accent/20" />
                <DropdownMenuLabel className="text-foreground/60 text-xs">Kategorien</DropdownMenuLabel>
                
                {categories.slice(0, 6).map(category => <DropdownMenuItem key={category.slug} asChild>
                    <Link to={`/blog?category=${category.slug}`} className="cursor-pointer text-foreground/80 hover:text-accent focus:text-accent">
                      <span className="flex-1">{category.name}</span>
                      <span className="text-xs text-muted-foreground">{category.count}</span>
                    </Link>
                  </DropdownMenuItem>)}
                
                {categories.length > 6 && <DropdownMenuItem asChild>
                    <Link to="/blog/kategorien" className="cursor-pointer text-accent hover:text-accent/80 focus:text-accent font-medium">
                      Alle {categories.length} Kategorien anzeigen →
                    </Link>
                  </DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            {user ? <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-foreground/80 hover:text-accent transition-colors cursor-pointer">
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-background border-accent/20 z-50" align="end">
                  <DropdownMenuLabel className="text-foreground">Mein Konto</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-accent/20" />
                  {isAdmin && <DropdownMenuItem asChild>
                      <Link to="/admin/blog" className="cursor-pointer text-foreground hover:text-accent focus:text-accent">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Backend</span>
                      </Link>
                    </DropdownMenuItem>}
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive hover:bg-destructive/10">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Abmelden</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> : <Link to="/auth" className="text-foreground/80 hover:text-accent transition-colors font-medium text-xs xl:text-sm flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Login
              </Link>}
            <Link to="/termin-buchen" className="inline-flex items-center justify-center px-3 xl:px-4 py-2 bg-transparent text-white border-2 border-accent rounded-md font-semibold text-xs xl:text-sm transition-all hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80">
              Beratung
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-foreground">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && <div className="lg:hidden pb-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <PrefetchLink to="/" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => {
              setIsOpen(false);
              window.scrollTo(0, 0);
            }}>
                Home
              </PrefetchLink>
              <Collapsible>
                <CollapsibleTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 w-full text-left flex items-center justify-between">
                  Cyberrisiken
                  <ChevronDown className="w-4 h-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-2 mt-2">
                  <Link to="/cyberrisiken" className="block text-foreground/70 hover:text-accent transition-colors py-1.5 font-medium flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <Grid3x3 className="h-4 w-4" />
                    Cyber-Risiken Übersicht
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Häufige Schadensszenarien:</div>
                  </div>
                  <Link to="/cyberversicherung/ransomware-schutz" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <ShieldAlert className="h-4 w-4 text-destructive" />
                    Ransomware-Angriffe
                  </Link>
                  <Link to="/cyberversicherung/datenverlust" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <Database className="h-4 w-4 text-destructive" />
                    Datenverlust & Datendiebstahl
                  </Link>
                  <Link to="/cyberversicherung/phishing-schutz" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <Mail className="h-4 w-4 text-destructive" />
                    Phishing-Attacken
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Finanzielle Risiken:</div>
                  </div>
                  <Link to="/cyberversicherung/dsgvo-bussgelder" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <FileWarning className="h-4 w-4 text-orange-500" />
                    DSGVO-Bußgelder
                  </Link>
                  <Link to="/cyberversicherung/betriebsunterbrechung" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <Activity className="h-4 w-4 text-orange-500" />
                    Betriebsunterbrechung
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Spezielle Bedrohungen:</div>
                  </div>
                  <Link to="/cyberversicherung/patientendaten-diebstahl" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <Heart className="h-4 w-4 text-yellow-600" />
                    Patientendaten-Diebstahl
                  </Link>
                </CollapsibleContent>
              </Collapsible>
              <Collapsible>
                <CollapsibleTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 w-full text-left flex items-center justify-between">
                  Praxistypen
                  <ChevronDown className="w-4 h-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-2 mt-2">
                  <Link to="/cyberversicherung/hausarztpraxis" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Hausarztpraxis
                  </Link>
                  <Link to="/cyberversicherung/zahnarztpraxis" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Zahnarztpraxis
                  </Link>
                  <Link to="/cyberversicherung/facharztpraxis" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Facharztpraxis
                  </Link>
                  <Link to="/cyberversicherung/mvz" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    MVZ
                  </Link>
                  <Link to="/cyberversicherung/klinik" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Klinik
                  </Link>
                </CollapsibleContent>
              </Collapsible>
              <PrefetchLink to="/tarifwissen" prefetchImages={[tarifHero]} className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => setIsOpen(false)}>
                Tarifwissen
              </PrefetchLink>
              <PrefetchLink to="/cyberversicherung/vergleichsrechner" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => setIsOpen(false)}>
                Vergleich
              </PrefetchLink>
              <Collapsible>
                <CollapsibleTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 w-full text-left flex items-center justify-between">
                  Beratung
                  <ChevronDown className="w-4 h-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-2 mt-2">
                  <Link to="/cyberversicherung/vergleichsrechner" className="block text-foreground/70 hover:text-accent transition-colors py-1.5 font-medium" onClick={() => setIsOpen(false)}>
                    Vergleichsrechner
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Cyberversicherung in Ihrer Stadt:</div>
                  </div>
                  <Link to="/cyberversicherung" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Alle Städte anzeigen
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Top Städte:</div>
                    <Link to="/cyberversicherung/berlin" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Berlin
                    </Link>
                    <Link to="/cyberversicherung/muenchen" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      München
                    </Link>
                    <Link to="/cyberversicherung/hamburg" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Hamburg
                    </Link>
                    <Link to="/cyberversicherung/koeln" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Köln
                    </Link>
                    <Link to="/cyberversicherung/frankfurt" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Frankfurt
                    </Link>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <Collapsible>
                <CollapsibleTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 w-full text-left flex items-center justify-between">
                  Versicherungen
                  <ChevronDown className="w-4 h-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-2 mt-2">
                  <Link to="/weitere-versicherungen" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Alle Versicherungen anzeigen
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Pflichtversicherungen:</div>
                    <Link to="/weitere-versicherungen/berufshaftpflicht" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Berufshaftpflicht
                    </Link>
                    <Link to="/weitere-versicherungen/betriebshaftpflicht" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Betriebshaftpflicht
                    </Link>
                  </div>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Empfohlen:</div>
                    <Link to="/weitere-versicherungen/inhaltsversicherung" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Inhaltsversicherung
                    </Link>
                    <Link to="/weitere-versicherungen/rechtsschutz" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Rechtsschutzversicherung
                    </Link>
                  </div>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Ergänzend:</div>
                    <Link to="/weitere-versicherungen/praxisausfall" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Praxisausfallversicherung
                    </Link>
                    <Link to="/weitere-versicherungen/elektronik" className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                      Elektronikversicherung
                    </Link>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <PrefetchLink to="/faq" prefetchImages={[faqHero]} className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => setIsOpen(false)}>
                FAQ
              </PrefetchLink>
              <Collapsible>
                <CollapsibleTrigger className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 w-full text-left flex items-center justify-between">
                  Blog
                  <ChevronDown className="w-4 h-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-2 mt-2">
                  <Link to="/blog" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Alle Artikel
                  </Link>
                  <Link to="/blog/kategorien" className="block text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                    Kategorien-Übersicht
                  </Link>
                  <div className="border-t border-accent/20 my-2 pt-2">
                    <div className="text-xs text-muted-foreground mb-2">Kategorien:</div>
                    {categories.slice(0, 5).map(category => <Link key={category.slug} to={`/blog?category=${category.slug}`} className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1.5" onClick={() => setIsOpen(false)}>
                        {category.name} ({category.count})
                      </Link>)}
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <PrefetchLink to="/erstinformation" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => setIsOpen(false)}>
                Erstinformation
              </PrefetchLink>
              <PrefetchLink to="/ueber-uns" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2" onClick={() => setIsOpen(false)}>
                Über uns
              </PrefetchLink>
              {user ? <>
                  {isAdmin && <Link to="/admin/blog" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                      <Settings className="h-4 w-4" />
                      Backend
                    </Link>}
                  <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-foreground/80 hover:text-accent justify-start">
                    <LogOut className="h-4 w-4 mr-2" />
                    Abmelden
                  </Button>
                </> : <Link to="/auth" className="text-foreground/80 hover:text-accent transition-colors font-medium py-2 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                  <Shield className="h-4 w-4" />
                  Login
                </Link>}
              <Link to="/termin-buchen" className="inline-flex items-center justify-center px-6 py-2 bg-transparent text-white border-2 border-accent rounded-md font-semibold text-sm transition-all hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80 w-full" onClick={() => setIsOpen(false)}>
                Jetzt beraten lassen
              </Link>
            </div>
          </div>}
        </div>
      </nav>
    </header>;
};
export default Navigation;